import { useCallback, useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import cloneDeep from "lodash/cloneDeep";
import lodashGet from "lodash/get";
import lodashSet from "lodash/set";
import { useForm } from "react-hook-form";
import type { DefaultValues, FieldPath, FieldValues, Resolver } from "react-hook-form";
import type { ZodSchema, ZodTypeDef } from "zod";

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value) && !(value instanceof Date);

const mergeDefaults = <TValue>(defaults: TValue, values: Partial<TValue>): TValue => {
  if (!isPlainRecord(defaults) || !isPlainRecord(values)) {
    return values === undefined ? defaults : (values as TValue);
  }

  const result: Record<string, unknown> = { ...defaults };

  Object.entries(values).forEach(([key, value]) => {
    const defaultValue = result[key];

    result[key] =
      isPlainRecord(defaultValue) && isPlainRecord(value)
        ? mergeDefaults(defaultValue, value)
        : value;
  });

  return result as TValue;
};

function normalizeDateFields<T extends FieldValues>(
  values: Partial<T>,
  dateKeys: FieldPath<T>[],
): Partial<T> {
  const result = cloneDeep(values);

  for (const key of dateKeys) {
    const val = lodashGet(result, key) as unknown;
    if (val) {
      const date = new Date(val as string | number | Date);
      if (!isNaN(date.getTime())) {
        lodashSet(result, key, date);
      }
    }
  }

  return result;
}

type UsePersistentFormProps<T extends FieldValues> = {
  schema: ZodSchema<T, ZodTypeDef, FieldValues>;
  defaultValues: T;
  storeValues: Partial<T>;
  setStoreValues: (data: Partial<T>) => void;
  resetStore: () => void;
  watch?: () => void;
  persistKey: string;
  dateKeys?: FieldPath<T>[]; // Optional list of fields to treat as dates
};

export const usePersistentForm = <T extends FieldValues>({
  schema,
  defaultValues,
  storeValues,
  setStoreValues,
  resetStore,
  persistKey,
  dateKeys = [],
}: UsePersistentFormProps<T>) => {
  // Merge values and normalize any date fields
  const mergedDefaults = mergeDefaults(defaultValues, storeValues);
  const normalizedDefaults = normalizeDateFields(mergedDefaults, dateKeys);

  const form = useForm<T>({
    mode: "onTouched",
    reValidateMode: "onBlur",
    resolver: zodResolver(schema as never) as Resolver<T>,
    defaultValues: normalizedDefaults as DefaultValues<T>,
  });

  const hydrate = useCallback(
    async (fetcher: () => Promise<Partial<T>>) => {
      const apiData = await fetcher();
      const normalized = normalizeDateFields(apiData, dateKeys);
      const mergedValues = mergeDefaults(defaultValues, normalized);

      setStoreValues(normalized);
      form.reset(mergedValues as DefaultValues<T>);

      await form.trigger();
    },
    [defaultValues, form.reset, setStoreValues, form.trigger, dateKeys],
  );

  const clear = useCallback(() => {
    resetStore();
    localStorage.removeItem(persistKey);

    const normalized = normalizeDateFields(defaultValues, dateKeys);
    form.reset(normalized as DefaultValues<T>);
    setStoreValues(normalized);
  }, [resetStore, form.reset, defaultValues, persistKey, setStoreValues, dateKeys]);

  return useMemo(() => ({ ...form, hydrate, clear, watch: form.watch }), [form, hydrate, clear]);
};
